/**
 * PublicFooter — reproduit fidèlement le footer MCP
 */

import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { useTranslation } from 'react-i18next';
import { isDesktop } from '@/AppMode';

const ExtIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" className="opacity-40 flex-shrink-0">
    <path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6M6 1h3m0 0v3m0-3L4.5 5.5"/>
  </svg>
);

const ColHeader = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-bold uppercase tracking-[.12em] text-white/40 mb-3.5 pb-2 border-b border-white/[0.06]">
    {children}
  </p>
);

const FootLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    className="block text-[13px] text-white/55 hover:text-white transition-colors mb-2">
    {children}
  </Link>
);

const FootLinkExt = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-1 text-[13px] text-white/55 hover:text-white transition-colors mb-2">
    {children} <ExtIcon />
  </a>
);

const FootLinkHref = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a href={href} className="block text-[13px] text-white/55 hover:text-white transition-colors mb-2">
    {children}
  </a>
);

const _FootLinkMail = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a href={href} className="block text-[13px] text-white/55 hover:text-white transition-colors mb-2">
    {children}
  </a>
);

export const PublicFooter = () => {
  // App desktop : pas de pied de page marketing
  if (isDesktop()) return null;
  const { t } = useTranslation();
  return (
    <footer className="bg-[#1c2333] border-t border-white/[0.08] pt-16 pb-8">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-9">

        {/* ── Top : 4 colonnes ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[240px_1fr_1fr_1fr] gap-12 mb-12">

          {/* Logo + tagline */}
          <div>
            <div className="w-[52px] h-[52px] rounded-[14px] bg-white flex items-center justify-center mb-3"
              style={{ boxShadow: '0 4px 16px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.9)' }}>
              <Logo className="w-[38px] h-[38px]" />
            </div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-serif text-[17px] font-bold text-white tracking-[-0.01em]">SmartLawyer</span>
            </div>
            <p className="text-[12px] text-white/55 leading-[1.7] max-w-[200px] mt-2">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Produit */}
          <div>
            <ColHeader>{t('footer.product')}</ColHeader>
            <FootLink to="/">{t('footer.search')}</FootLink>
            <FootLinkHref href="https://smartlawyer.ai/mcp/">{t('footer.mcp_integration')}</FootLinkHref>
            <FootLink to="/legal-graph">Legal Graph</FootLink>
            <FootLink to="/sources">{t('footer.sources')}</FootLink>
            <FootLinkHref href="https://smartlawyer.ai/faq/">{t('footer.faq')}</FootLinkHref>
            <FootLink to="/pricing">{t('nav.pricing')}</FootLink>
          </div>

          {/* Développeurs */}
          <div>
            <ColHeader>{t('footer.developers')}</ColHeader>
            <FootLinkHref href="https://smartlawyer.ai/documentation/">{t('footer.documentation')}</FootLinkHref>
            <FootLinkHref href="https://smartlawyer.ai/playground/">Playground</FootLinkHref>
            <FootLinkExt href="/docs">{t('menu.rest_api')}</FootLinkExt>
          </div>

          {/* Entreprise */}
          <div>
            <ColHeader>{t('footer.company')}</ColHeader>
            <FootLink to="/qui-sommes-nous">{t('menu.about')}</FootLink>
            <FootLink to="/contact">{t('nav.contact')}</FootLink>
            <FootLink to="/politique-cookies">{t('footer.cookies')}</FootLink>
            <FootLink to="/mentions-legales">{t('footer.legal_notice')}</FootLink>
            <FootLinkHref href="https://smartlawyer.ai/privacy/">{t('footer.privacy')}</FootLinkHref>
          </div>
        </div>

        {/* ── Bottom ── */}
        <div className="pt-8 border-t border-white/[0.07] flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/55">
          <span>{t('footer.copyright')}</span>
          <span className="text-white/30">MCP Legal Graph v4.0 · 13 Tools · Streamable HTTP</span>
        </div>

      </div>
    </footer>
  );
};